import {ErrorInfo} from 'react';
import {ReporterRequestPayload, EventType} from '../server/@types';

/**
 * reportError depends on a crumb available on window.ha
 * as well as a errorReporter.url available on window
 */
type DecoratedWindow = Window & {
    ha?: {
        crumb?: string;
    };
    errorReporterConfig: {url: string};
};

/**
 * Error along with info from a componentDidCatch as emitted from a Error Boundary component in React
 */
type ComponentError = {
    error: Error;
    errorInfo: ErrorInfo;
};

export type ErrorReportData = {
    name: string;
    requestMarker?: string;
    logData?: object;
    componentError?: ComponentError;
};

export type SuccessReportData = {
    name: string;
    logData?: object;
};

// eslint-disable-next-line complexity
function postEvent(data: ErrorReportData, type: EventType): Promise<Response> {
    const {ha, errorReporterConfig} = (window &&
        ((window as unknown) as DecoratedWindow)) || {
        ha: {},
        errorReporterConfig: {}
    };

    // TODO: need remove this in favor of the hapi plugin consuming JWT CSRF
    // See https://github.homeawaycorp.com/shopping/react-error-reporter/issues/8
    const crumb = (ha && ha.crumb) || '';
    const url = errorReporterConfig.url || '';

    if (!url) {
        throw new Error('no error reporter config found');
    }

    const payload: ReporterRequestPayload = {
        name: data.name,
        logData: data.logData,
        type,
        ...(!!data.componentError && {
            componentError: {
                message: data.componentError.error.message,
                stack: data.componentError.error.stack,
                errorInfo: data.componentError.errorInfo
            }
        })
    };

    if (data.requestMarker) {
        payload.requestMarker = data.requestMarker;
    }

    return fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            Accept: 'application/json, text/plain, */*',
            'Content-Type': 'application/json;charset=UTF-8',
            ...(crumb && {'x-csrf-token': crumb})
        },
        body: JSON.stringify(payload)
    });
}

const eventTimeout: {[key: string]: NodeJS.Timeout | void} = {};

export function reportEvent(
    data: ErrorReportData | SuccessReportData,
    type: EventType
): void {
    if (typeof window === 'undefined') {
        return;
    }

    const cacheKey = `${type}:${data.name}`;

    if (eventTimeout[cacheKey]) {
        return;
    }
    postEvent(data, type);

    eventTimeout[cacheKey] = setTimeout(() => {
        const timeout = eventTimeout[cacheKey];
        if (timeout) {
            eventTimeout[cacheKey] = clearTimeout(timeout);
        }
    }, 200);
}

/**
 * Reports errors from client changes. Debounces error reporting for noisy errors by data.name
 * @param ErrorReportData params to request be logged as an error report
 */
export const reportError = (data: ErrorReportData): void =>
    reportEvent(data, 'ERROR');

/**
 * Reports success events from client changes. Debounces success reporting for noisey events by data.name
 * @param SuccessReportData params to request be logged as an success report
 */
export const reportSuccess = (data: SuccessReportData): void =>
    reportEvent(data, 'SUCCESS');
